import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { motion } from "framer-motion";
import { 
  Phone, 
  Link as LinkIcon, 
  Download, 
  Image as ImageIcon, 
  CheckCircle2
} from "lucide-react";
import { FaLinkedin, FaInstagram, FaWhatsapp, FaEnvelope, FaGlobe, FaStar } from "react-icons/fa";
import CountUp from "react-countup";
import { useEffect } from "react";

// 👉 Put your logo files in src/assets/
//    - src/assets/moodale.png        (full logo, header)
//    - src/assets/modale-mark.png        (optional mark, footer watermark)
import moodaleLogo from "./assets/moodale.png";
import modaleMark from "./assets/modale-mark.png"; // if missing, it will fallback to the main logo below

/* Helpers */
const onlyDigits = (s = "") => s.replace(/\D+/g, "");
const buildWhatsappUrl = ({ cc, phone, prefillMsg, message }) => {
  const num = onlyDigits(`${cc}${phone}`);
  if (!num) return "";
  const base = `https://wa.me/${num}`;
  const params = prefillMsg && message ? `?text=${encodeURIComponent(message)}` : "";
  return `${base}${params}`;
};
const buildTelUrl = ({ cc, phone }) => {
  const num = onlyDigits(`${cc}${phone}`);
  return num ? `tel:+${num}` : "";
};
const sanitizeLink = (url) => {
  if (!url) return "";
  const hasProtocol = /^(https?:)?\/\//i.test(url);
  return hasProtocol ? url : `https://${url}`;
};

/** Fixed preview size (does not change with use case) */
const PREVIEW_PX = 256;

/** Render config for each use-case (export pixels, ECC, logo scale) */
const getRenderConfig = (useCase) => {
  switch (useCase) {
    case "small":
      return { exportPx: 512,  level: "H", logoScale: 0.18 }; // 18% of QR side
    case "large":
      return { exportPx: 2048, level: "M", logoScale: 0.22 }; // crisper for print
    case "normal":
    default:
      return { exportPx: 1024, level: "Q", logoScale: 0.20 };
  }
};

export default function App() {
  const [mode, setMode] = useState("phone");
  const [useCase, setUseCase] = useState("normal");
  const [logoOverlay, setLogoOverlay] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState("");

  // Phone
  const [callCC, setCallCC] = useState("+91");
  const [callPhone, setCallPhone] = useState("");

  // WhatsApp
  const [waCC, setWaCC] = useState("+91");
  const [waPhone, setWaPhone] = useState("");
  const [prefillMsg, setPrefillMsg] = useState(true);
  const [waMsg, setWaMsg] = useState("Hi! I found your QR and wanted to connect.");

  // Link
  const [linkUrl, setLinkUrl] = useState("");

  const canvasRef = useRef(null);

  const qrValue = useMemo(() => {
    if (mode === "phone") return buildTelUrl({ cc: callCC, phone: callPhone });
    if (mode === "whatsapp") return buildWhatsappUrl({ cc: waCC, phone: waPhone, prefillMsg, message: waMsg });
    if (mode === "link") return sanitizeLink(linkUrl);
    return "";
  }, [mode, callCC, callPhone, waCC, waPhone, prefillMsg, waMsg, linkUrl]);

  const isValid = useMemo(() => {
    if (mode === "phone") return onlyDigits(`${callCC}${callPhone}`).length >= 8;
    if (mode === "whatsapp") return onlyDigits(`${waCC}${waPhone}`).length >= 8;
    if (mode === "link") return !!linkUrl.trim();
    return false;
  }, [mode, callCC, callPhone, waCC, waPhone, linkUrl]);

  const { exportPx, level, logoScale } = useMemo(
    () => getRenderConfig(useCase),
    [useCase]
  );

  // Build imageSettings to embed the logo INTO the canvas (not overlaid)
  const imageSettings = useMemo(() => {
    if (!logoOverlay || !logoDataUrl) return undefined;
    return {
      src: logoDataUrl,
      height: Math.round(exportPx * logoScale),
      width:  Math.round(exportPx * logoScale),
      excavate: true, // clear the modules below the logo for better readability
    };
  }, [logoOverlay, logoDataUrl, exportPx, logoScale]);

  const onUploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(String(reader.result));
    reader.readAsDataURL(file);
  };

  const downloadPNG = () => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `qr-${mode}-${useCase}.png`;
    link.click();
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Soft Grid + Vignette Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[#0a0c12]"/>
        {/* faint grid */}
        <div className="absolute inset-0 opacity-[0.06]"
             style={{ backgroundImage: `linear-gradient(to right, rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.6) 1px, transparent 1px)`,
                      backgroundSize: '48px 48px' }} />
        {/* center glow + vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(40%_30%_at_50%_15%,rgba(99,102,241,0.2),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_120%,rgba(0,0,0,0.7),transparent)]" />
      </div>

      {/* keep your content wrapper below */}
      <div className="relative z-10">
        {/* ---------- HEADER (Centered MOODALE logo + tagline) ---------- */}
        <header className="relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-4 pt-12 pb-8 text-center">
            {/* Moodale Logo + Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex flex-col items-center"
            >
              <img
                src="/moodale-logo.png"
                alt="Moodale"
                className="h-16 md:h-24 w-auto object-contain"
                onError={(e) => {
                  // Fallback to the imported logo if the public one fails
                  e.target.src = moodaleLogo;
                }}
              />
              <p className="text-xs md:text-sm text-slate-300 tracking-wide mt-2">
                digital marketing & strategy agency
              </p>
            </motion.div>

            {/* Product badge */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
              className="mt-6 flex items-center justify-center"
            >
              <span className="inline-flex items-center gap-2 rounded-full border bg-gray-900/70 px-4 py-2 text-xs md:text-sm text-slate-200 shadow-sm border-gray-700/50">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">Moodale-free QR Generator</span>
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className="mt-8 font-extrabold tracking-tight leading-[1.1] text-3xl md:text-5xl lg:text-6xl drop-shadow-[0_1px_0_rgba(0,0,0,0.35)]"
            >
              <span className="font-['Space_Grotesk'] bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-pink-400">
                QR code
              </span>{" "}
              <span className="text-white/90">that never</span>{" "}
              <span className="text-sky-400">expires</span>
              <span className="text-white/90">— built just for you</span>
            </motion.h1>

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
              className="mt-6 max-w-2xl mx-auto text-slate-300 text-base md:text-lg leading-relaxed"
            >
              Create unlimited QR codes for free. No sign-up. No gimmicks. Just fast,
              privacy-friendly generation for calls, WhatsApp, and links — ready for
              print or screens.
            </motion.p>
          </div>
        </header>

        {/* ---------- BUILDER (form + preview) ---------- */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="rounded-xl border bg-gray-900/70 shadow-xl backdrop-blur-sm border-gray-700/50 overflow-hidden"
          >
            <div className="p-5 md:p-6 border-b border-gray-700/50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-white">Make your QR in seconds</h2>
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-blue-900/30 text-blue-300 border border-blue-800/50">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Real-time preview</span>
                </div>
              </div>
            </div>

            <div className="p-5 md:p-6 grid lg:grid-cols-2 gap-8">
              {/* LEFT - Form Section */}
              <div className="space-y-6">
                {/* Mode Selector Tabs */}
                <div className="flex gap-2 p-1 bg-gray-800/50 rounded-lg">
                  <button
                    onClick={() => setMode("phone")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all ${
                      mode === "phone"
                        ? "bg-gray-700 text-white shadow"
                        : "text-slate-300 hover:text-white hover:bg-gray-700/50"
                    }`}
                  >
                    <Phone className="w-4 h-4" />
                    <span>Phone</span>
                  </button>
                  <button
                    onClick={() => setMode("whatsapp")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all ${
                      mode === "whatsapp"
                        ? "bg-gray-700 text-white shadow"
                        : "text-slate-300 hover:text-white hover:bg-gray-700/50"
                    }`}
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                  <button
                    onClick={() => setMode("link")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all ${
                      mode === "link"
                        ? "bg-gray-700 text-white shadow"
                        : "text-slate-300 hover:text-white hover:bg-gray-700/50"
                    }`}
                  >
                    <LinkIcon className="w-4 h-4" />
                    <span>Link</span>
                  </button>
                </div>

                {/* Form Content */}
                <div className="space-y-5">
                  {mode === "phone" && (
                    <div className="grid grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="text-xs font-medium text-slate-200 mb-1.5 block">Country code</label>
                        <input
                          value={callCC}
                          onChange={(e) => setCallCC(e.target.value)}
                          placeholder="+91"
                          className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-slate-200 mb-1.5 block">Phone number</label>
                        <input
                          inputMode="tel"
                          placeholder="9XXXXXXX1"    // light hint only
                          value={callPhone}
                          onChange={(e) => setCallPhone(e.target.value)}
                          className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                        />
                      </div>
                    </div>
                  )}

                  {mode === "whatsapp" && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-3 gap-3 items-end">
                        <div>
                          <label className="text-xs font-medium text-slate-200 mb-1.5 block">Country code</label>
                          <input
                            value={waCC}
                            onChange={(e) => setWaCC(e.target.value)}
                            placeholder="+91"
                            className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-slate-200 mb-1.5 block">WhatsApp number</label>
                          <input
                            inputMode="tel"
                            placeholder="9XXXXXXX1"    // light hint only
                            value={waPhone}
                            onChange={(e) => setWaPhone(e.target.value)}
                            className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          id="prefill"
                          type="checkbox"
                          checked={prefillMsg}
                          onChange={(e) => setPrefillMsg(e.target.checked)}
                          className="h-4 w-4 rounded bg-gray-800 border-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <label htmlFor="prefill" className="text-sm font-medium text-slate-200">Pre-fill message</label>
                      </div>

                      {prefillMsg && (
                        <div>
                          <label className="text-xs font-medium text-slate-200 mb-1.5 block">Message</label>
                          <input
                            value={waMsg}
                            onChange={(e) => setWaMsg(e.target.value)}
                            placeholder="Type a custom message"
                            className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {mode === "link" && (
                    <div>
                      <label className="text-xs font-medium text-slate-200 mb-1.5 block">URL</label>
                      <input
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                      />
                    </div>
                  )}

                  {/* Options */}
                  <div className="pt-2 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-200 mb-1.5 block">Use case (size)</label>
                        <select
                          value={useCase}
                          onChange={(e) => setUseCase(e.target.value)}
                          className="h-10 w-full rounded-lg border px-3 text-sm bg-gray-800/80 text-white border-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all appearance-none"
                        >
                          <option value="small">Small — cards/labels</option>
                          <option value="normal">Normal — screens/mobile</option>
                          <option value="large">Large — high-density print</option>
                        </select>
                      </div>

                      <div className="flex items-end justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={logoOverlay}
                            onChange={(e) => setLogoOverlay(e.target.checked)}
                            className="h-4 w-4 rounded bg-gray-800 border-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                          />
                          <label className="text-sm font-medium text-slate-200">Logo overlay</label>
                        </div>

                        {logoOverlay && (
                          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-slate-200 bg-gray-800/80 hover:bg-gray-700/80 px-2.5 py-1.5 rounded-lg transition-colors">
                            <input type="file" accept="image/*" className="hidden" onChange={onUploadLogo} />
                            <ImageIcon className="w-3.5 h-3.5" /> Upload
                          </label>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={downloadPNG}
                        disabled={!isValid}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                          isValid 
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md hover:shadow-blue-500/20" 
                            : "bg-gray-700 cursor-not-allowed text-gray-400"
                        }`}
                        title={isValid ? "Download PNG" : "Fill required fields first"}
                      >
                        <Download className="w-4 h-4" />
                        Download PNG
                      </button>

                      <a
                        href={isValid ? qrValue : "#"}
                        target="_blank"
                        rel="noreferrer"
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                          isValid 
                            ? "hover:bg-gray-800 bg-gray-900/80 text-white border-gray-700 hover:border-gray-600 shadow hover:shadow-gray-500/10" 
                            : "pointer-events-none opacity-50 text-gray-400 border-gray-700"
                        }`}
                      >
                        Test link
                      </a>
                    </div>

                    {!isValid && (
                      <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-900/20 px-3 py-2 rounded-lg">
                        <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                        <span>Enter required details to generate QR code</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT: live preview (no default QR) */}
              <div>
                {/* Preview card (only the canvas and title live here) */}
                <div className="rounded-lg border border-dashed bg-gray-900/80 border-gray-700/50">
                  <div className="p-5 border-b border-gray-700/50 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Live Preview</h3>
                    <div className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-green-900/30 text-green-300">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                      <span>Live</span>
                    </div>
                  </div>

                  {/* Make interior a vertical stack so nothing sits beside the canvas */}
                  <div className="p-5 flex flex-col items-center">
                    <div
                      className="relative bg-gray-900/90 rounded-lg border border-gray-700/50 shadow-lg"
                      style={{ width: PREVIEW_PX, height: PREVIEW_PX }}
                    >
                      {isValid ? (
                        <>
                          <QRCodeCanvas
                            id="qr-canvas"
                            value={qrValue}
                            size={exportPx}          // true pixel density per useCase
                            level={level}            // ECC per useCase
                            includeMargin
                            renderAs="canvas"
                            ref={canvasRef}
                            bgColor="rgba(17, 24, 39, 0.95)"
                            fgColor="#ffffff"
                            imageSettings={imageSettings}
                            // ↓ Visually clamp to fixed preview size, regardless of exportPx
                            style={{ width: PREVIEW_PX, height: PREVIEW_PX }}
                          />
                        </>
                      ) : (
                        <div className="absolute inset-0 grid place-items-center text-xs text-slate-400 px-4">
                          <div className="text-center">
                            <div className="mb-2 text-2xl">🔍</div>
                            <div>No preview yet</div>
                            <div className="mt-1">Enter details to generate a QR</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* MOVE META OUTSIDE THE CARD so it always appears BELOW the preview */}
                <div className="mt-4 text-sm text-slate-300 space-y-1">
                  <div className="flex gap-2">
                    <span className="font-medium">Type:</span>
                    <span className="capitalize">{mode}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium">Use case:</span>
                    <span className="capitalize">{useCase}</span>
                  </div>
                  <div className="flex gap-2 items-start">
                    <span className="font-medium">Target:</span>
                    {/* Wrap long targets so they never overlap the preview */}
                    <span className="break-all text-slate-400">{isValid ? qrValue : "—"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* NEW: compact USP bar below builder */}
            <div className="px-5 md:px-6 pb-5 border-t bg-gray-800/50 text-xs sm:text-sm text-slate-300 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 backdrop-blur-sm border-gray-700/50">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                <span><strong>Free forever:</strong> Unlimited scans</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                <span><strong>Never expires:</strong> No tracking</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                <span><strong>Print-ready:</strong> High error-correction</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                <span><strong>Privacy-friendly:</strong> Generated locally</span>
              </div>
            </div>
          </motion.div>
        </main>

        {/* ---------- FOOTER (watermark + social links) ---------- */}
        <footer className="relative overflow-hidden text-center text-slate-300 pt-12 pb-8">
          {/* watermark background */}
          <div
            className="absolute inset-0 opacity-3 bg-no-repeat bg-center bg-contain"
            style={{
              backgroundImage: `url(${modaleMark || moodaleLogo})`,
              filter: 'grayscale(100%)',
            }}
            aria-hidden="true"
          />

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
            {/* logo */}
            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <img
                src="/moodale-logo.png"
                onError={(e) => {
                  // Fallback to the imported logo if the public one fails
                  e.target.src = moodaleLogo;
                }}
                alt="Moodale"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </motion.div>
            
            <motion.h3
              className="text-sm uppercase tracking-wide text-slate-400 font-medium mt-2"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              marketing & strategy agency
            </motion.h3>

            {/* highlighted review CTA */}
            <motion.a
              href="https://g.page/r/CW3Sc5-WIod_EBM/review"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-6 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold px-6 py-3 rounded-full text-sm hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all shadow-lg hover:shadow-amber-500/30"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.2 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaStar className="text-amber-200" />
              Leave a Review
            </motion.a>

            {/* social links */}
            <motion.div
              className="mt-10 flex flex-wrap justify-center gap-6 text-slate-300"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, y: 10 },
                show: { opacity: 1, y: 0, transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
              }}
            >
              <motion.a
                href="https://www.linkedin.com/company/moodale/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-blue-400 transition-all duration-200 hover:scale-110"
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
              >
                <FaLinkedin className="w-5 h-5" />
              </motion.a>
              
              <motion.a
                href="https://www.instagram.com/moodale_/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-pink-400 transition-all duration-200 hover:scale-110"
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
              >
                <FaInstagram className="w-5 h-5" />
              </motion.a>
              
              <motion.a
                href="https://wa.me/917983738443"
                target="_blank"
                rel="noreferrer"
                className="hover:text-green-400 transition-all duration-200 hover:scale-110"
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
              >
                <FaWhatsapp className="w-5 h-5" />
              </motion.a>
              
              <motion.a
                href="mailto:moodale2020@gmail.com"
                target="_blank"
                rel="noreferrer"
                className="hover:text-blue-300 transition-all duration-200 hover:scale-110"
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
              >
                <FaEnvelope className="w-5 h-5" />
              </motion.a>
              
              <motion.a
                href="https://www.moodale.in"
                target="_blank"
                rel="noreferrer"
                className="hover:text-slate-100 transition-all duration-200 hover:scale-110"
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
              >
                <FaGlobe className="w-5 h-5" />
              </motion.a>
            </motion.div>

            <motion.p
              className="mt-12 text-xs text-slate-500"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              © {new Date().getFullYear()} <strong className="text-white">Moodale</strong> — marketing & strategy agency
            </motion.p>
          </div>
        </footer>
      </div>
    </div>
  );
}
